/**
 * API 服务配置
 */
import axios from 'axios'
import { ElMessage } from 'element-plus'
import store from '@/store'
import router from '@/router'

// 创建axios实例
const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://skyc.xryai.cn/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  config => {
    // 添加token - 优先从store获取，如果没有则从Cookie获取
    let token = store.getters.token
    if (!token) {
      // 从Cookie中获取token
      const cookies = document.cookie.split(';')
      const adminTokenCookie = cookies.find(c => c.trim().startsWith('admin_token='))
      if (adminTokenCookie) {
        token = adminTokenCookie.split('=')[1]
        console.log('从Cookie获取token:', token)
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 显示加载状态
    if (config.showLoading !== false) {
      // 可以在这里添加全局loading
    }

    console.log('API请求:', config.method?.toUpperCase(), config.url, config.data || config.params)
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => {
    const { data } = response

    console.log('API响应:', response.config.url, data)

    // 统一处理响应 - 根据API文档，成功响应的code为1
    if (data.code === 1) {
      return data
    } else {
      // 业务错误 - 不自动显示错误消息，让调用方处理
      const message = data.msg || '操作失败'
      console.warn('API业务错误:', message, data)
      return data // 返回原始数据，让调用方判断
    }
  },
  error => {
    console.error('响应错误:', error)

    // 处理HTTP错误
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 401:
          ElMessage.error('登录已过期，请重新登录')
          store.commit('CLEAR_USER_DATA')
          router.push('/login')
          break
        case 403:
          ElMessage.error('权限不足')
          break
        case 404:
          ElMessage.error('请求的资源不存在')
          break
        case 500:
          ElMessage.error('服务器内部错误')
          break
        default:
          ElMessage.error(data?.msg || `请求失败 (${status})`)
      }
    } else if (error.code === 'ECONNABORTED') {
      ElMessage.error('请求超时，请稍后重试')
    } else {
      ElMessage.error('网络错误，请检查网络连接')
    }

    return Promise.reject(error)
  }
)

// ================================
// 认证相关接口
// ================================

/**
 * 管理员登录
 */
export const login = (data) => {
  return api.post('/admin/auth/login', data)
}

/**
 * 管理员退出登录
 */
export const logout = () => {
  return api.post('/admin/auth/logout')
}

/**
 * 获取管理员信息
 */
export const getAdminProfile = () => {
  return api.get('/admin/auth/profile')
}

// ================================
// 用户管理接口
// ================================

/**
 * 获取用户列表
 */
export const getUserList = (params) => {
  return api.get('/admin/users', { params })
}

/**
 * 调整用户Token余额
 */
export const adjustUserTokens = (userId, data) => {
  return api.put(`/admin/users/${userId}/tokens`, data)
}

/**
 * 更新用户状态
 */
export const updateUserStatus = (userId, data) => {
  return api.put(`/admin/users/${userId}/status`, data)
}

/**
 * 获取用户详细信息 (管理员权限)
 */
export const getUserProfile = (userId) => {
  return api.get(`/admin/users/${userId}/profile`)
}

/**
 * 更新用户信息 (管理员权限)
 */
export const updateUserProfile = (userId, data) => {
  return api.put(`/admin/users/${userId}/profile`, data)
}

/**
 * 获取用户Token消费记录 (管理员权限)
 */
export const getUserTokenRecords = (userId, params) => {
  return api.get(`/admin/users/${userId}/token-records`, { params })
}

/**
 * 获取用户统计信息 (管理员权限)
 */
export const getUserStatistics = (userId) => {
  return api.get(`/users/statistics`, {
    headers: { 'X-Admin-User-Id': userId } // 管理员查看指定用户统计
  })
}



// ================================
// 统计数据接口
// ================================

/**
 * 获取概览统计
 */
export const getOverviewStats = () => {
  return api.get('/admin/statistics/overview')
}

/**
 * 获取用户增长趋势
 */
export const getUserGrowthTrend = (days = 7) => {
  return api.get('/admin/statistics/user-growth', { params: { days } })
}

/**
 * 获取Token消费趋势
 */
export const getTokenConsumptionTrend = (days = 7) => {
  return api.get('/admin/statistics/token-consumption', { params: { days } })
}

/**
 * 获取所有Token消费记录（用于统计）
 */
export const getAllTokenRecords = (pageSize = 1000) => {
  return api.get('/admin/users/1/token-records', { params: { page: 1, pageSize } })
}

// ================================
// 文件上传接口
// ================================

/**
 * 上传图片
 */
export const uploadImage = (file, type) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('type', type)

  return api.post('/upload/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

// ================================
// 素材管理接口
// ================================

/**
 * 获取人脸素材列表
 */
export const getFaceMaterials = (params) => {
  return api.get('/materials/faces', { params })
}

/**
 * 添加人脸素材
 */
export const addFaceMaterial = (data) => {
  return api.post('/materials/faces', data)
}

/**
 * 更新人脸素材
 */
export const updateFaceMaterial = (id, data) => {
  return api.put(`/materials/faces/${id}`, data)
}

/**
 * 删除人脸素材
 */
export const deleteFaceMaterial = (id) => {
  return api.delete(`/materials/faces/${id}`)
}

/**
 * 获取背景素材列表
 */
export const getBackgroundMaterials = (params) => {
  return api.get('/materials/backgrounds', { params })
}

/**
 * 添加背景素材
 */
export const addBackgroundMaterial = (data) => {
  return api.post('/materials/backgrounds', data)
}

/**
 * 更新背景素材
 */
export const updateBackgroundMaterial = (id, data) => {
  return api.put(`/materials/backgrounds/${id}`, data)
}

/**
 * 删除背景素材
 */
export const deleteBackgroundMaterial = (id) => {
  return api.delete(`/materials/backgrounds/${id}`)
}

// ================================
// 操作日志接口
// ================================

/**
 * 获取操作日志
 */
export const getOperationLogs = (params) => {
  return api.get('/admin/logs', { params })
}

// ================================
// 系统配置接口
// ================================

/**
 * 获取所有系统配置
 */
export const getSystemConfigs = () => {
  return api.get('/config/admin')
}

/**
 * 根据配置键获取配置
 */
export const getConfigByKey = (configKey) => {
  return api.get(`/config/admin/${configKey}`)
}

/**
 * 创建新配置
 */
export const createConfig = (data) => {
  return api.post('/config/admin', data)
}

/**
 * 更新配置
 */
export const updateConfig = (id, data) => {
  return api.put(`/config/admin/${id}`, data)
}

/**
 * 删除配置
 */
export const deleteConfig = (id) => {
  return api.delete(`/config/admin/${id}`)
}

/**
 * 批量更新配置
 */
export const batchUpdateConfigs = (data) => {
  return api.put('/config/admin/batch', data)
}

// 导出axios实例
export default api
